import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import LinearProgress from "@mui/material/LinearProgress";
import { ProductThumb } from "./recent-order";
import { GET_SELLER_TOP_PRODUCTS_CURRENT_MONTH } from "../../../../Utils/Queries";

function formatTopProductRevenue(amount) {
  const n = Number(amount);
  if (Number.isNaN(n)) return "—";
  return `$${n.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

export const TopProductsCard = () => {
  const { data, loading, error } = useQuery(GET_SELLER_TOP_PRODUCTS_CURRENT_MONTH);
  const topProducts = useMemo(() => {
    const raw = data?.getSellerTopProductsCurrentMonth;
    const list = Array.isArray(raw) ? raw : [];
    const slice = list.slice(0, 5);
    const maxQty = Math.max(
      1,
      ...slice.map((row) => Number(row.quantitySold) || 0)
    );
    return slice.map((row, i) => {
      const qty = Number(row.quantitySold) || 0;
      return {
        key: row.product?._id ?? `top-${i}`,
        rank: i + 1,
        name: row.product?.name || "—",
        sku: row.product?.reference || row.product?.alias || "—",
        image: row.product?.images?.[0],
        price: formatTopProductRevenue(row.totalAmountSold),
        sold: qty,
        progress: Math.round((qty / maxQty) * 100),
      };
    });
  }, [data]);

  const showEmpty = !loading && !error && topProducts.length === 0;

  return (
  <div className="bg-white rounded-[14px] border border-[#EFEFEF] p-5 h-[450px] flex flex-col">
    <div className="mb-4 shrink-0">
      <h3 className="text-[15px] font-semibold text-[#151515] my-0">Top Products</h3>
      <p className="text-[12.5px] text-[#6B7280] mt-1">By revenue this month</p>
    </div>

    <div className="flex-1 min-h-0 flex flex-col mt-5">
      {error && !loading ? (
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <p className="text-[14px] font-semibold text-[#151515] my-0">
            Couldn&apos;t load top products
          </p>
          <p className="text-[12.5px] text-[#6B7280] mt-2 my-0 max-w-[280px] leading-snug">
            Something went wrong while fetching this month&apos;s data. Try
            refreshing the page.
          </p>
        </div>
      ) : loading ? (
        <div className="flex-1 flex items-center justify-center px-4">
          <p className="text-[13px] text-[#9CA3AF] my-0">Loading top products…</p>
        </div>
      ) : showEmpty ? (
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <p className="text-[14px] font-semibold text-[#151515] my-0">
            No sales this month yet
          </p>
          <p className="text-[12.5px] text-[#6B7280] mt-2 my-0 max-w-[280px] leading-snug">
            Once your products sell, your best performers for the month will
            appear here.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3.5 overflow-y-auto">
          {topProducts.map((item) => (
            <div key={item.key} className="flex items-center gap-3 mb-3">
              <span className="text-[13px] text-[#6B7280] w-4 shrink-0">
                {item.rank}
              </span>
              <ProductThumb src={item.image} alt="" />
              <div className="min-w-[110px]">
                <p className="text-[13px] font-medium text-[#151515] my-0 leading-tight">
                  {item.name}
                </p>
                <p className="text-[11.5px] text-[#9CA3AF] mt-0.5">{item.sku}</p>
              </div>
              <div className="flex-1 px-2">
                <LinearProgress
                  variant="determinate"
                  value={item.progress}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    backgroundColor: "#F1F1F4",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#7F56D9",
                      borderRadius: 999,
                    },
                  }}
                />
              </div>
              <div className="text-right shrink-0 min-w-[60px]">
                <p className="text-[13px] font-semibold text-[#151515] my-0 leading-tight">
                  {item.price}
                </p>
                <p className="text-[11.5px] text-[#9CA3AF] mt-0.5">
                  {item.sold} sold
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
  );
};